import React from 'react';
import Helmet from 'react-helmet';

import { SITE_BASE_URL } from '../constants';
import { SanityImageType } from '../graphql-fragments/SanityImage';
import { urlJoin } from './utils';

// The full Organization node lives on the homepage (components/StructuredData.tsx);
// other pages reference it by this @id.
export const ORGANIZATION_ID = `${SITE_BASE_URL}/#organization`;
export const ORGANIZATION_NAME = 'Heed Capital';
export const ORGANIZATION_LOGO_URL = `${SITE_BASE_URL}/heed-capital-logo.svg`;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JsonLdObject = Record<string, any>;

export function getOrganizationRef(): JsonLdObject {
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: ORGANIZATION_NAME,
    url: `${SITE_BASE_URL}/`,
    logo: ORGANIZATION_LOGO_URL,
  };
}

export function getCanonicalUrl(pathname: string): string {
  return urlJoin(SITE_BASE_URL, pathname);
}

export function getImageUrl(image?: SanityImageType): string | undefined {
  return image?.asset?.url;
}

// Strips tracking params so `sameAs` points at the canonical profile URL.
export function cleanProfileUrl(url: string): string {
  return url.split('?')[0];
}

interface ArticleSchemaParams {
  headline: string;
  description?: string;
  imageUrl?: string;
  datePublished?: string;
  dateModified?: string;
  pathname: string;
  lang: string;
}

export function buildArticleSchema({
  headline,
  description,
  imageUrl,
  datePublished,
  dateModified,
  pathname,
  lang,
}: ArticleSchemaParams): JsonLdObject {
  const canonicalUrl = getCanonicalUrl(pathname);
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline,
    ...(description && { description }),
    ...(imageUrl && { image: imageUrl }),
    ...(datePublished && { datePublished }),
    ...(dateModified && { dateModified }),
    inLanguage: lang,
    url: canonicalUrl,
    mainEntityOfPage: canonicalUrl,
    // Insights have no author field, so the company is both author and publisher.
    author: getOrganizationRef(),
    publisher: getOrganizationRef(),
  };
}

interface FundSchemaParams {
  name: string;
  description?: string;
  imageUrl?: string;
  category?: string;
  goldenVisaEligible?: boolean;
  awards?: Array<string>;
  // Modelled as properties, not aggregateRating, which would imply a review count.
  ratings?: Array<{ label: string; value: number }>;
  characteristics?: Array<{ name: string; value: string }>;
  pathname: string;
}

export function buildFundSchema({
  name,
  description,
  imageUrl,
  category,
  goldenVisaEligible,
  awards,
  ratings,
  characteristics,
  pathname,
}: FundSchemaParams): JsonLdObject {
  const additionalProperty: Array<JsonLdObject> = [
    ...(characteristics || []).map(c => ({
      '@type': 'PropertyValue',
      name: c.name,
      value: c.value,
    })),
    ...(ratings || []).map(r => ({
      '@type': 'PropertyValue',
      name: r.label,
      value: String(r.value),
    })),
    ...(goldenVisaEligible
      ? [{ '@type': 'PropertyValue', name: 'Golden Visa eligible', value: 'true' }]
      : []),
  ];

  return {
    '@context': 'https://schema.org',
    '@type': 'FinancialProduct',
    name,
    ...(description && { description }),
    ...(imageUrl && { image: imageUrl }),
    ...(category && { category }),
    url: getCanonicalUrl(pathname),
    provider: getOrganizationRef(),
    ...(awards && awards.length > 0 && { award: awards }),
    ...(additionalProperty.length > 0 && { additionalProperty }),
  };
}

interface ServiceSchemaParams {
  name: string;
  description?: string;
  imageUrl?: string;
  serviceType?: string;
  pathname: string;
}

export function buildServiceSchema({
  name,
  description,
  imageUrl,
  serviceType,
  pathname,
}: ServiceSchemaParams): JsonLdObject {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    ...(description && { description }),
    ...(imageUrl && { image: imageUrl }),
    ...(serviceType && { serviceType }),
    url: getCanonicalUrl(pathname),
    provider: getOrganizationRef(),
  };
}

interface PersonSchemaInput {
  name: string;
  jobTitle?: string;
  imageUrl?: string;
  linkedinUrl?: string;
}

export function buildPersonListSchema(persons: Array<PersonSchemaInput>): JsonLdObject | null {
  if (persons.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@graph': persons.map(person => ({
      '@type': 'Person',
      name: person.name,
      ...(person.jobTitle && { jobTitle: person.jobTitle }),
      ...(person.imageUrl && { image: person.imageUrl }),
      ...(person.linkedinUrl && { sameAs: [cleanProfileUrl(person.linkedinUrl)] }),
      worksFor: getOrganizationRef(),
    })),
  };
}

export function buildFaqSchema(
  items: Array<{ question: string; answer: string }>,
): JsonLdObject | null {
  const validItems = items.filter(item => item.question && item.answer);
  if (validItems.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: validItems.map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };
}

export function JsonLd({ data }: { data: JsonLdObject | null }): React.ReactElement {
  if (!data) return <></>;
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}
